import dayjs from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';

export enum EmailVerificationCodeStatus {
    Live = 'LIVE',
    Used = 'USED'
}

export class EmailVerificationCode extends DataModel {
  static readonly TYPE_ID: string = '0d9e2cfeb6c44eb4864e16092d166451';

  constructor(
    public readonly uniqueId: string,
    public readonly userId: string,
    public readonly email: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly expirationDate: dayjs.Dayjs,
    public readonly usedAt: dayjs.Dayjs | null,
    public readonly status: EmailVerificationCodeStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return EmailVerificationCode.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): EmailVerificationCode {
    return new EmailVerificationCode(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'userId'),
        MapUtils.get<string>(map, 'email'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDate(MapUtils.get<number>(map, 'expirationDate')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'usedAt')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), EmailVerificationCodeStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'userId': this.userId,
      'email': this.email,
      'creationDate': context.encodeDate(this.creationDate),
      'expirationDate': context.encodeDate(this.expirationDate),
      'usedAt': context.encodeDateOrNull(this.usedAt),
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new EmailVerificationCode(
        this.uniqueId,
        this.userId,
        this.email,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.expirationDate),
        TimeZone.cloneDateOrNull(this.usedAt),
        this.status,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.userId === this.userId &&
        other.email === this.email &&
        other.creationDate === this.creationDate &&
        other.expirationDate === this.expirationDate &&
        other.usedAt === this.usedAt &&
        other.status === this.status;
  }

  copyWith(fields?: {
    uniqueId?: string,
    userId?: string,
    email?: string,
    creationDate?: dayjs.Dayjs,
    expirationDate?: dayjs.Dayjs,
    usedAt?: dayjs.Dayjs | null,
    status?: EmailVerificationCodeStatus,
  }): this {
    return new EmailVerificationCode(
        fields?.uniqueId ?? this.uniqueId,
        fields?.userId ?? this.userId,
        fields?.email ?? this.email,
        fields?.creationDate ?? TimeZone.cloneDate(this.creationDate),
        fields?.expirationDate ?? TimeZone.cloneDate(this.expirationDate),
        fields?.usedAt === null ?
          null:
          fields?.usedAt ?? this.usedAt,
        fields?.status ?? this.status,
    ) as this;
  }
}

export class EmailVerificationCodeBuilder extends DataModelBuilder<EmailVerificationCode> {
  build(context: SerializationContext, map: SerializedData): EmailVerificationCode {
    return EmailVerificationCode.fromMap(context, map);
  }
}
